import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, addDoc, deleteDoc, doc } from 'firebase/firestore';
import {
  Box, TextField, Autocomplete, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Typography, List,
  ListItem, ListItemText, ListItemSecondaryAction
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';

export default function RoleSelect({ value, onChange, departmentId, size = 'medium' }) {
  const [roles, setRoles] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (departmentId) loadRoles();
  }, [departmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadRoles() {
    const snap = await getDocs(
      collection(db, 'departments', departmentId, 'roles')
    );
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    list.sort((a, b) => a.name.localeCompare(b.name));
    setRoles(list);
  }

  async function handleAddRole() {
    if (!newRole.trim()) return;
    if (roles.find(r => r.name.toLowerCase() === newRole.trim().toLowerCase())) {
      setNewRole('');
      return;
    }
    setLoading(true);
    await addDoc(collection(db, 'departments', departmentId, 'roles'), {
      name: newRole.trim(),
      createdAt: new Date()
    });
    setNewRole('');
    await loadRoles();
    setLoading(false);
  }

  async function handleDeleteRole(roleId) {
    await deleteDoc(doc(db, 'departments', departmentId, 'roles', roleId));
    loadRoles();
  }

  if (!departmentId) {
    return (
      <TextField
        label="Roll i uppdrag"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        size={size}
        fullWidth
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flex: 1 }}>
      <Autocomplete
        freeSolo
        fullWidth
        options={roles.map(r => r.name)}
        value={value || ''}
        onInputChange={(e, newValue) => onChange(newValue)}
        onChange={(e, newValue) => onChange(newValue || '')}
        renderInput={(params) => (
          <TextField {...params} label="Roll i uppdrag" size={size} />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Typography variant="body2">{option}</Typography>
          </Box>
        )}
      />
      <IconButton
        size="small"
        onClick={() => setManageOpen(true)}
        sx={{ mt: size === 'small' ? 0.5 : 1, flexShrink: 0 }}
        title="Hantera roller">
        <SettingsIcon fontSize="small" />
      </IconButton>

      {/* Hantera roller-dialog */}
      <Dialog open={manageOpen} onClose={() => setManageOpen(false)}
        maxWidth="xs" fullWidth>
        <DialogTitle>Hantera roller</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Roller för detta affärsområde
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
            <TextField
              size="small" fullWidth
              label="Ny roll"
              value={newRole}
              onChange={e => setNewRole(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddRole(); }}
              placeholder="t.ex. BIM-samordnare"
            />
            <Button variant="contained" onClick={handleAddRole}
              disabled={loading || !newRole.trim()}
              sx={{ flexShrink: 0 }}>
              <AddIcon />
            </Button>
          </Box>

          <List dense>
            {roles.map(role => (
              <ListItem key={role.id}
                sx={{ bgcolor: 'background.default', borderRadius: 1, mb: 0.5 }}>
                <ListItemText primary={role.name} />
                <ListItemSecondaryAction>
                  <IconButton size="small" edge="end" color="error"
                    onClick={() => handleDeleteRole(role.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {roles.length === 0 && (
              <ListItem>
                <ListItemText secondary="Inga roller tillagda ännu." />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageOpen(false)}>Stäng</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}